import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Area,
  AreaChart,
} from "recharts";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3001";

const PERIODOS = [
  { valor: "quinzenal", label: "Quinzenal" },
  { valor: "mensal", label: "Mensal" },
  { valor: "trimestral", label: "Trimestral" },
  { valor: "anual", label: "Anual" },
];

function nomePeriodo(periodo) {
  const item = PERIODOS.find((p) => p.valor === periodo);
  return item?.label || "Mensal";
}

export default function DetalheRede() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [atualizandoId, setAtualizandoId] = useState(null);
  const [relatorioAberto, setRelatorioAberto] = useState(null);
  const [carregandoRelatorio, setCarregandoRelatorio] = useState(false);
  const [periodo, setPeriodo] = useState("mensal");
  const [assistidoAberto, setAssistidoAberto] = useState(false);
  const [salvandoAssistido, setSalvandoAssistido] = useState(false);
  const [ajustePeriodoAberto, setAjustePeriodoAberto] = useState(false);
  const [salvandoAjustePeriodo, setSalvandoAjustePeriodo] = useState(false);
  const [lancamentoAssistido, setLancamentoAssistido] = useState({
    data: new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }),
    faturamentoBruto: "",
    vendasNovas: "",
    renovacoes: "",
    leads: "",
    cobradosRenovacao: "",
    gastoTrafego: "",
    creditoServidor: "",
    observacao: "",
  });
  const [ajustePeriodo, setAjustePeriodo] = useState({
    periodo: "mensal",
    dataReferencia: new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" }),
    faturamentoDesejado: "",
    observacao: "",
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    carregar();
  }, [id, periodo]);

  async function carregar() {
    try {
      setCarregando(true);

      // Sempre busca o estado atual: esse painel é usado para corrigir valores que
      // precisam refletir no ranking imediatamente, sem reaproveitar uma resposta antiga.
      const res = await fetch(`${API_BASE}/minha-rede/${id}?periodo=${periodo}&atualizadoEm=${Date.now()}`, {
        headers: { Authorization: token },
        cache: "no-store",
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Erro ao carregar painel.");
        return;
      }

      // Fallback de segurança:
      // Se o detalhe /minha-rede/:id vier sem relatórios, busca a fila geral
      // de pendentes do usuário logado e filtra somente o usuário aberto.
      // Isso destrava aprovação sem mexer nos cálculos do dashboard.
      if (!Array.isArray(data.relatoriosRecebidos) || data.relatoriosRecebidos.length === 0) {
        try {
          const filaRes = await fetch(`${API_BASE}/relatorios-recebidos?status=pendente`, {
            headers: { Authorization: token },
          });

          const fila = await filaRes.json();

          if (filaRes.ok && Array.isArray(fila)) {
            const emailAberto = String(data?.usuario?.email || "").toLowerCase();

            const pendentesDoUsuarioAberto = fila.filter((relatorio) => {
              const emailRelatorio = String(
                relatorio?.usuario ||
                  relatorio?.usuarioInfo?.email ||
                  ""
              ).toLowerCase();

              return emailRelatorio && emailAberto && emailRelatorio === emailAberto;
            });

            if (pendentesDoUsuarioAberto.length > 0) {
              data.relatoriosRecebidos = pendentesDoUsuarioAberto;
            }
          }
        } catch (fallbackErr) {
          console.log("Fallback relatórios recebidos falhou:", fallbackErr);
        }
      }

      setDados(data);
    } catch (err) {
      console.log(err);
      alert("Erro ao carregar painel.");
    } finally {
      setCarregando(false);
    }
  }

  async function alterarStatus(relatorioId, status, tipoRelatorio = "diario") {
    let observacao = "";

    if (status === "rejeitado") {
      observacao = window.prompt("Informe o motivo da rejeição:") || "";
    }

    if (status === "aguardando_correcao") {
      observacao = window.prompt("Informe o que precisa ser corrigido:") || "";
    }

    try {
      setAtualizandoId(relatorioId);

      const res = await fetch(
        `${API_BASE}/relatorios-recebidos/${relatorioId}/status`,
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status,
            observacao,
            tipoRelatorio,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Erro ao atualizar relatório.");
        return;
      }

      await carregar();

      if (relatorioAberto?._id === relatorioId) {
        await abrirRelatorio(relatorioId, tipoRelatorio);
      }
    } catch (err) {
      console.log(err);
      alert("Erro ao atualizar relatório.");
    } finally {
      setAtualizandoId(null);
    }
  }

  async function abrirRelatorio(relatorioId, tipoRelatorio = "diario") {
    try {
      setCarregandoRelatorio(true);

      const tipo =
        tipoRelatorio === "consolidado" || tipoRelatorio === "semanal"
          ? "consolidado"
          : "diario";

      const res = await fetch(
        `${API_BASE}/relatorios-recebidos/${relatorioId}?tipo=${tipo}`,
        {
          headers: { Authorization: token },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Erro ao abrir relatório.");
        return;
      }

      setRelatorioAberto({
        ...data,
        tipoRelatorio: tipo,
      });
    } catch (err) {
      console.log(err);
      alert("Erro ao abrir relatório.");
    } finally {
      setCarregandoRelatorio(false);
    }
  }

  function atualizarCampoAssistido(campo, valor) {
    setLancamentoAssistido((anterior) => ({ ...anterior, [campo]: valor }));
  }

  async function salvarLancamentoAssistido(event) {
    event.preventDefault();

    try {
      setSalvandoAssistido(true);
      const res = await fetch(`${API_BASE}/minha-rede/${id}/lancamento-assistido`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(lancamentoAssistido),
      });
      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Não foi possível salvar o lançamento assistido.");
        return;
      }

      alert("Lançamento da franquia salvo. O ranking será atualizado com este valor.");
      setAssistidoAberto(false);
      await carregar();
    } catch (err) {
      console.log(err);
      alert("Erro ao salvar o lançamento assistido.");
    } finally {
      setSalvandoAssistido(false);
    }
  }

  function atualizarAjustePeriodo(campo, valor) {
    setAjustePeriodo((anterior) => ({ ...anterior, [campo]: valor }));
  }

  async function salvarAjustePeriodo(event) {
    event.preventDefault();

    try {
      setSalvandoAjustePeriodo(true);
      const res = await fetch(`${API_BASE}/minha-rede/${id}/ajuste-faturamento-periodo`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ajustePeriodo),
      });
      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Não foi possível ajustar o faturamento do período.");
        return;
      }

      alert(`${data.message}\nTotal anterior: ${moeda(data.faturamentoAtual)}\nAjuste aplicado: ${moeda(data.diferenca)}`);
      setAjustePeriodoAberto(false);
      await carregar();
    } catch (err) {
      console.log(err);
      alert("Erro ao ajustar o faturamento do período.");
    } finally {
      setSalvandoAjustePeriodo(false);
    }
  }

  function moeda(valor) {
    return Number(valor || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  function moedaCurta(valor) {
    const numero = Number(valor || 0);

    if (Math.abs(numero) >= 1000000) {
      return `R$ ${(numero / 1000000).toFixed(1).replace(".", ",")}M`;
    }

    if (Math.abs(numero) >= 1000) {
      return `R$ ${(numero / 1000).toFixed(0)}k`;
    }

    return `R$ ${numero.toFixed(0)}`;
  }

  function percentual(valor) {
    return `${Number(valor || 0).toFixed(1)}%`;
  }

  function formatarCrescimentoInfo(info) {
    if (!info || info.disponivel === false || info.percentual === null || info.percentual === undefined) {
      return {
        texto: "Em formação",
        detalhe: "Aguardando histórico suficiente para comparar",
        status: "inicio",
      };
    }

    const perc = Number(info.percentual || 0);

    return {
      texto: info.texto || `${perc >= 0 ? "+" : ""}${perc.toFixed(1)}%`,
      detalhe: info.detalhe || "Comparação com período anterior equivalente",
      status: perc >= 0 ? "positivo" : "negativo",
    };
  }

  function calcularCrescimento(lista, chave) {
    const crescimentoInfo = dados?.resumo?.crescimentoInfo;

    if (!crescimentoInfo || crescimentoInfo.disponivel === false) {
      return {
        texto: "Em formação",
        detalhe: "Aguardando histórico suficiente para comparar",
        status: "inicio",
      };
    }

    if (chave === "faturamento") {
      return formatarCrescimentoInfo(crescimentoInfo);
    }

    return {
      texto: "Em formação",
      detalhe: "Comparativo será liberado com mais histórico",
      status: "inicio",
    };
  }

  function calcularTotaisRelatorio(relatorio) {
    const faturamento = Number(relatorio?.faturamento || 0);
    const totalGastos = Number(relatorio?.totalGastos || 0);

    const totalDevolucoes =
      relatorio?.totalDevolucoes !== undefined && relatorio?.totalDevolucoes !== null
        ? Number(relatorio.totalDevolucoes || 0)
        : Array.isArray(relatorio?.devolucoes)
        ? relatorio.devolucoes.reduce((acc, item) => acc + Number(item.valor || 0), 0)
        : 0;

    return {
      faturamento,
      faturamentoBruto: Number(relatorio?.faturamentoBruto ?? (faturamento + totalDevolucoes)),
      totalDevolucoes,
      vendasNovas: Number(relatorio?.vendasNovas || 0),
      renovacoes: Number(relatorio?.renovacoes || 0),
      leads: Number(relatorio?.leads || 0),
      totalVendas:
        Number(relatorio?.totalVendas || 0) ||
        Number(relatorio?.vendasNovas || 0) + Number(relatorio?.renovacoes || 0),
      totalGastos,
      lucroLiquido:
        relatorio?.lucroLiquido !== undefined && relatorio?.lucroLiquido !== null
          ? Number(relatorio.lucroLiquido || 0)
          : faturamento - totalGastos,
    };
  }

  const usuario = dados?.usuario || {};

  /*
    BASE OFICIAL DO DETALHE DA REDE

    O Dashboard usa resumoMeuGrupo.hoje / resumoMeuGrupo.mes.
    Então o DetalheRede também precisa usar essa mesma base,
    e NÃO o resumo principal antigo quando ele vier inflado pelo backend.

    Regra:
    - Card principal = usuário aberto + colaboradores diretos
    - Rede abaixo fica apenas nos gráficos/resumo de rede
    - Não soma resumoTudo/resumoRede no card principal
  */
  const resumoMeuGrupoPeriodo =
    dados?.resumoMeuGrupo?.periodo ||
    dados?.resumoMeuGrupo?.mes ||
    dados?.resumoIndividual?.periodo ||
    dados?.resumoIndividual?.mes ||
    dados?.resumo || {};

  const resumoMeuGrupoHoje =
    dados?.resumoMeuGrupo?.hoje ||
    dados?.resumoIndividual?.hoje ||
    {};

  const resumoBackend = dados?.resumo || {};

  const resumo = {
    ...resumoBackend,

    faturamentoHoje: Number(resumoMeuGrupoHoje?.faturamento || 0),

    faturamentoMes: Number(resumoMeuGrupoPeriodo?.faturamento || 0),
    lucroMes: Number(resumoMeuGrupoPeriodo?.lucro || 0),
    gastosMes: Number(resumoMeuGrupoPeriodo?.gastos || 0),
    vendasMes: Number(resumoMeuGrupoPeriodo?.vendas || 0),
    vendasNovasMes: Number(resumoMeuGrupoPeriodo?.vendasNovas || 0),
    renovacoesMes: Number(resumoMeuGrupoPeriodo?.renovacoes || 0),

    faturamentoPeriodo: Number(resumoMeuGrupoPeriodo?.faturamento || 0),
    lucroPeriodo: Number(resumoMeuGrupoPeriodo?.lucro || 0),
    gastosPeriodo: Number(resumoMeuGrupoPeriodo?.gastos || 0),
    vendasPeriodo: Number(resumoMeuGrupoPeriodo?.vendas || 0),
    vendasNovasPeriodo: Number(resumoMeuGrupoPeriodo?.vendasNovas || 0),
    renovacoesPeriodo: Number(resumoMeuGrupoPeriodo?.renovacoes || 0),
    leadsPeriodo: Number(resumoMeuGrupoPeriodo?.leads || 0),
  };

  const relatorios = dados?.relatoriosRecebidos || [];
  const periodoInfo = dados?.periodoInfo || {};
  const periodoTitulo = periodoInfo.titulo || nomePeriodo(periodo);
  const periodoDescricao = periodoInfo.descricao || "Dados filtrados do período selecionado";

  const graficoGeral = useMemo(() => {
    const base =
      dados?.evolucaoMeuGrupo ||
      dados?.evolucaoIndividual ||
      dados?.evolucaoMensal ||
      [];

    return base.map((item) => ({
      mes: formatarMes(item.mes),
      faturamento: Number(item.faturamento || 0),
      lucro: Number(item.lucro || 0),
      gastos: Number(item.gastos || 0),
    }));
  }, [dados]);

  const graficoIndividual = graficoGeral;

  const graficoRede = useMemo(() => {
    if (Array.isArray(dados?.evolucaoRede) && dados.evolucaoRede.length > 0) {
      return dados.evolucaoRede.map((item) => ({
        mes: formatarMes(item.mes),
        faturamento: Number(item.faturamento || 0),
        lucro: Number(item.lucro || 0),
        gastos: Number(item.gastos || 0),
      }));
    }

    const rels = dados?.ultimosLancamentos || [];
    const somenteRede = rels.filter((r) => r.usuario !== usuario.email);

    const agrupado = {};

    somenteRede.forEach((r) => {
      if (!r.data) return;

      const chave = r.data.slice(0, 7);

      if (!agrupado[chave]) {
        agrupado[chave] = {
          mes: formatarMes(chave),
          faturamento: 0,
          lucro: 0,
          gastos: 0,
        };
      }

      agrupado[chave].faturamento += Number(r.faturamento || 0);
      agrupado[chave].lucro += Number(r.lucroLiquido || 0);
      agrupado[chave].gastos += Number(r.totalGastos || 0);
    });

    return Object.values(agrupado);
  }, [dados, usuario.email]);

  if (carregando) {
    return (
      <>
        <style>{css}</style>
        <div className="detalheContainer">
          <div className="loadingBox">Carregando painel da franquia...</div>
        </div>
      </>
    );
  }

  if (!dados) {
    return (
      <>
        <style>{css}</style>
        <div className="detalheContainer">
          <div className="loadingBox">Painel não encontrado.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>

      <div className="detalheContainer">
        <div className="topBar">
          <button className="btnVoltar" onClick={() => navigate(-1)}>
            ← Voltar
          </button>

          <button className="btnAtualizar" onClick={carregar}>
            Atualizar
          </button>
        </div>

        <div className="heroDetalhe">
          <div className="heroLeft">
            <div className="avatar">
              {usuario.logoEmpresa ? (
                <img src={usuario.logoEmpresa} alt={usuario.nomeEmpresa} />
              ) : (
                <span>{String(usuario.nomeEmpresa || "N").charAt(0)}</span>
              )}
            </div>

            <div>
              <span className="miniTitle">PAINEL DA FRANQUIA</span>
              <h1>{usuario.nomeEmpresa}</h1>
              <p>{usuario.email}</p>

              <div className="badges">
                <span className="badge role">{usuario.role}</span>
                <span className={`badge saude ${resumo.saude}`}>
                  Saúde: {resumo.saude || "neutra"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {dados?.podePreencherAssistido && usuario.role !== "colaborador" && (
          <section className="assistidoBox">
            <div>
              <span className="miniTitle">ACESSO DE MASTER</span>
              <h2>Preenchimento assistido</h2>
              <p>
                Preencha o lançamento desta franquia sem trocar de conta. O sistema
                registra que a atualização foi feita por você.
              </p>
            </div>
            <div className="assistidoAcoes">
              <button
                className="btnAssistido"
                onClick={() => setAssistidoAberto((aberto) => !aberto)}
              >
                {assistidoAberto ? "Fechar diário" : "Preencher diário"}
              </button>
              <button
                className="btnAssistido secundario"
                onClick={() => setAjustePeriodoAberto((aberto) => !aberto)}
              >
                {ajustePeriodoAberto ? "Fechar ajuste" : "Ajustar total do período"}
              </button>
            </div>
          </section>
        )}

        {assistidoAberto && dados?.podePreencherAssistido && (
          <form className="formAssistido" onSubmit={salvarLancamentoAssistido}>
            <div className="formAssistidoHeader">
              <div>
                <span className="miniTitle">LANÇAMENTO ASSISTIDO</span>
                <h2>{usuario.nomeEmpresa}</h2>
                <p>Use para completar um dia que a franquia deixou em branco.</p>
              </div>
              <span className="assistidoSeguro">✓ Registrado com sua autoria</span>
            </div>

            <div className="assistidoGrid">
              <CampoAssistido label="Data" type="date" value={lancamentoAssistido.data} onChange={(valor) => atualizarCampoAssistido("data", valor)} required />
              <CampoAssistido label="Faturamento bruto" type="number" step="0.01" min="0" value={lancamentoAssistido.faturamentoBruto} onChange={(valor) => atualizarCampoAssistido("faturamentoBruto", valor)} required />
              <CampoAssistido label="Vendas novas" type="number" min="0" value={lancamentoAssistido.vendasNovas} onChange={(valor) => atualizarCampoAssistido("vendasNovas", valor)} />
              <CampoAssistido label="Renovações" type="number" min="0" value={lancamentoAssistido.renovacoes} onChange={(valor) => atualizarCampoAssistido("renovacoes", valor)} />
              <CampoAssistido label="Leads" type="number" min="0" value={lancamentoAssistido.leads} onChange={(valor) => atualizarCampoAssistido("leads", valor)} />
              <CampoAssistido label="Clientes cobrados para renovar" type="number" min="0" value={lancamentoAssistido.cobradosRenovacao} onChange={(valor) => atualizarCampoAssistido("cobradosRenovacao", valor)} />
              <CampoAssistido label="Tráfego pago" type="number" step="0.01" min="0" value={lancamentoAssistido.gastoTrafego} onChange={(valor) => atualizarCampoAssistido("gastoTrafego", valor)} />
              <CampoAssistido label="Créditos do servidor" type="number" step="0.01" min="0" value={lancamentoAssistido.creditoServidor} onChange={(valor) => atualizarCampoAssistido("creditoServidor", valor)} />
            </div>

            <label className="campoAssistido campoObservacao">
              <span>Observação da correção (opcional)</span>
              <textarea value={lancamentoAssistido.observacao} onChange={(event) => atualizarCampoAssistido("observacao", event.target.value)} placeholder="Ex.: preenchido porque a franquia não lançou no dia." maxLength="500" />
            </label>

            <div className="assistidoAviso">
              Este recurso não altera dias já fechados ou aprovados e não mexe nos cálculos do ranking.
            </div>
            <button className="btnSalvarAssistido" type="submit" disabled={salvandoAssistido}>
              {salvandoAssistido ? "Salvando..." : "Salvar lançamento da franquia"}
            </button>
          </form>
        )}

        {ajustePeriodoAberto && dados?.podePreencherAssistido && (
          <form className="formAssistido ajustePeriodoForm" onSubmit={salvarAjustePeriodo}>
            <div className="formAssistidoHeader">
              <div>
                <span className="miniTitle">AJUSTE DE FATURAMENTO</span>
                <h2>Definir total correto do período</h2>
                <p>
                  Informe o total final que deveria aparecer. A diferença será calculada
                  automaticamente e entrará no ranking sem refazer os lançamentos dia a dia.
                </p>
              </div>
              <span className="assistidoSeguro">✓ Ajuste rastreável</span>
            </div>

            <div className="assistidoGrid">
              <label className="campoAssistido">
                <span>Período</span>
                <select value={ajustePeriodo.periodo} onChange={(event) => atualizarAjustePeriodo("periodo", event.target.value)}>
                  <option value="quinzenal">Quinzenal</option>
                  <option value="mensal">Mensal</option>
                </select>
              </label>
              <CampoAssistido label="Data para registrar o ajuste" type="date" value={ajustePeriodo.dataReferencia} onChange={(valor) => atualizarAjustePeriodo("dataReferencia", valor)} required />
              <CampoAssistido label="Faturamento total correto" type="number" step="0.01" min="0" value={ajustePeriodo.faturamentoDesejado} onChange={(valor) => atualizarAjustePeriodo("faturamentoDesejado", valor)} required />
            </div>

            <label className="campoAssistido campoObservacao">
              <span>Motivo do ajuste (opcional)</span>
              <textarea value={ajustePeriodo.observacao} onChange={(event) => atualizarAjustePeriodo("observacao", event.target.value)} placeholder="Ex.: franquia informou R$ 500 que faltaram no fechamento mensal." maxLength="500" />
            </label>

            <div className="assistidoAviso">
              Para aumentar o total: informe o valor final correto. Para reduzir um total, use o formulário diário e corrija o dia responsável pelo excesso.
            </div>
            <button className="btnSalvarAssistido" type="submit" disabled={salvandoAjustePeriodo}>
              {salvandoAjustePeriodo ? "Calculando ajuste..." : "Aplicar total correto"}
            </button>
          </form>
        )}

        <div className="periodoPainel">
          <div>
            <span className="miniTitle">PERÍODO DE ANÁLISE</span>
            <h2>{periodoTitulo}</h2>
            <p>{periodoDescricao}</p>
          </div>

          <div className="periodoTabs">
            {PERIODOS.map((item) => (
              <button
                key={item.valor}
                className={periodo === item.valor ? "ativo" : ""}
                onClick={() => setPeriodo(item.valor)}
                disabled={carregando && periodo === item.valor}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <h2 className="tituloSecao">📊 Resumo da franquia</h2>

        <div className="cardsGrid">
          <Card title="Faturamento hoje" value={moeda(resumo.faturamentoHoje)} />
          <Card title="Faturamento período" value={moeda(resumo.faturamentoPeriodo ?? resumo.faturamentoMes)} />
          <Card title="Lucro período" value={moeda(resumo.lucroPeriodo ?? resumo.lucroMes)} destaque />
          <Card title="Gastos período" value={moeda(resumo.gastosPeriodo ?? resumo.gastosMes)} />
          <Card title="Vendas período" value={resumo.vendasPeriodo ?? resumo.vendasMes ?? 0} />
          <Card title="Renovações" value={resumo.renovacoesPeriodo ?? resumo.renovacoesMes ?? 0} />
          <Card
            title="Crescimento"
            value={formatarCrescimentoInfo(resumo.crescimentoInfo).texto}
            destaque
          />
        </div>

        <h2 className="tituloSecao">🚀 Crescimento da franquia</h2>

        <div className="crescimentoGrid">
          <Growth
            title="Crescimento faturamento"
            value={calcularCrescimento(graficoIndividual, "faturamento")}
          />
          <Growth
            title="Crescimento lucro"
            value={calcularCrescimento(graficoIndividual, "lucro")}
          />
          <Growth
            title="Evolução de gastos"
            value={calcularCrescimento(graficoIndividual, "gastos")}
          />
        </div>

        <h2 className="tituloSecao">📈 Minha evolução individual — {periodoTitulo}</h2>

        <Grafico
          data={graficoIndividual}
          moeda={moeda}
          moedaCurta={moedaCurta}
          vazio="Sem dados suficientes ainda."
          titulo={`Minha evolução — ${periodoTitulo}`}
          subtitulo="Faturamento, lucro e gastos do resultado direto no período selecionado"
        />

        <h2 className="tituloSecao">🌐 Evolução da rede — {periodoTitulo}</h2>

        <Grafico
          data={graficoRede}
          moeda={moeda}
          moedaCurta={moedaCurta}
          vazio="Sem dados suficientes da rede ainda."
          titulo={`Evolução da rede — ${periodoTitulo}`}
          subtitulo="Faturamento, lucro e gastos da equipe abaixo desta franquia no período selecionado"
        />

        <h2 className="tituloSecao">📄 Relatórios recebidos</h2>

        <div className="bloco">
          <div className="blocoHeader">
            <h2>Relatórios Recebidos</h2>
            <span className="contador">{relatorios.length}</span>
          </div>

          {relatorios.length === 0 ? (
            <div className="vazioClaro">Nenhum relatório recebido ainda.</div>
          ) : (
            relatorios.map((relatorio) => {
              const tipo = relatorio.tipoRelatorio || "diario";
              const totais = calcularTotaisRelatorio(relatorio);

              return (
                <div key={relatorio._id} className="relatorioCard">
                  <div className="relatorioTop">
                    <div>
                      <strong>{relatorio.nomeEmpresa || relatorio.usuario}</strong>
                      <p>
                        {tipo === "consolidado"
                          ? `📊 Consolidado • ${relatorio.data}`
                          : `📅 Diário • ${relatorio.data}`}
                      </p>
                    </div>

                    <span
                      className={`statusRelatorio ${relatorio.statusAprovacao}`}
                    >
                      {traduzirStatus(relatorio.statusAprovacao)}
                    </span>
                  </div>

                  <div className="relatorioValores">
                    <span>💰 {moeda(totais.faturamento)}</span>
                    <span>✅ {moeda(totais.lucroLiquido)}</span>
                    <span>📉 {moeda(totais.totalGastos)}</span>
                    <span>🆕 {totais.vendasNovas || 0}</span>
                    <span>🔁 {totais.renovacoes || 0}</span>
                  </div>

                  {relatorio.observacaoAprovacao && (
                    <div className="observacao">
                      {relatorio.observacaoAprovacao}
                    </div>
                  )}

                  <div className="acoesRelatorio">
                    <button
                      className="btnAbrirRelatorio"
                      onClick={() => abrirRelatorio(relatorio._id, tipo)}
                    >
                      Ver completo
                    </button>

                    <button
                      className="btnAprovar"
                      disabled={atualizandoId === relatorio._id}
                      onClick={() =>
                        alterarStatus(relatorio._id, "aprovado", tipo)
                      }
                    >
                      Aprovar
                    </button>

                    <button
                      className="btnCorrecao"
                      disabled={atualizandoId === relatorio._id}
                      onClick={() =>
                        alterarStatus(relatorio._id, "aguardando_correcao", tipo)
                      }
                    >
                      Correção
                    </button>

                    <button
                      className="btnRejeitar"
                      disabled={atualizandoId === relatorio._id}
                      onClick={() =>
                        alterarStatus(relatorio._id, "rejeitado", tipo)
                      }
                    >
                      Rejeitar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {relatorioAberto && (
        <ModalRelatorio
          relatorio={relatorioAberto}
          moeda={moeda}
          fechar={() => setRelatorioAberto(null)}
          alterarStatus={alterarStatus}
          atualizandoId={atualizandoId}
          carregando={carregandoRelatorio}
          calcularTotaisRelatorio={calcularTotaisRelatorio}
        />
      )}
    </>
  );
}

function CampoAssistido({ label, value, onChange, ...props }) {
  return (
    <label className="campoAssistido">
      <span>{label}</span>
      <input {...props} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Card({ title, value, destaque }) {
  return (
    <div className="card">
      <p>{title}</p>
      <h2 className={destaque ? "verde" : ""}>{value}</h2>
    </div>
  );
}

function Growth({ title, value }) {
  const info =
    typeof value === "object"
      ? value
      : {
          texto: value,
          detalhe: "Aguardando evolução",
          status: "neutro",
        };

  return (
    <div className={`growthCard ${info.status}`}>
      <div className="growthTopo">
        <span>{title}</span>
        <small>
          {info.status === "positivo"
            ? "↗"
            : info.status === "negativo"
            ? "↘"
            : info.status === "inicio"
            ? "◆"
            : "●"}
        </small>
      </div>

      <strong>{info.texto}</strong>
      <p>{info.detalhe}</p>
    </div>
  );
}

function Grafico({ data, moeda, moedaCurta, vazio, titulo, subtitulo }) {
  if (!data || data.length === 0) {
    return (
      <div className="graficoBox vazioPremium">
        <div className="vazioIcone">📊</div>
        <h3>Sem dados suficientes ainda</h3>
        <p>{vazio}</p>
      </div>
    );
  }

  const maiorFat = Math.max(...data.map((d) => d.faturamento || 0));

  const mediaFat =
    data.reduce((acc, item) => acc + Number(item.faturamento || 0), 0) /
    data.length;

  const melhorMes =
    data.find((item) => item.faturamento === maiorFat)?.mes || "-";

  const dadosGrafico =
    data.length === 1
      ? [
          { ...data[0], mes: "", fake: true },
          data[0],
          { ...data[0], mes: "", fake: true },
        ]
      : data;

  return (
    <div className="graficoWrapper">
      <div className="graficoTop">
        <div>
          <h3>{titulo}</h3>
          <p>{subtitulo}</p>
        </div>

        <div className="graficoStats">
          <div className="miniStat">
            <span>Maior faturamento</span>
            <strong>{moeda(maiorFat)}</strong>
          </div>

          <div className="miniStat">
            <span>Média mensal</span>
            <strong>{moeda(mediaFat)}</strong>
          </div>

          <div className="miniStat">
            <span>Melhor mês</span>
            <strong>{melhorMes}</strong>
          </div>
        </div>
      </div>

      <div className="graficoBox">
        <ResponsiveContainer width="100%" height={390}>
          <AreaChart
            data={dadosGrafico}
            margin={{ top: 15, right: 10, left: -15, bottom: 5 }}
          >
            <defs>
              <linearGradient id="fatGradientRede" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.42} />
                <stop offset="55%" stopColor="#3b82f6" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
              </linearGradient>

              <linearGradient id="lucroGradientRede" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.24} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.01} />
              </linearGradient>

              <linearGradient id="gastosGradientRede" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f97316" stopOpacity={0.16} />
                <stop offset="100%" stopColor="#f97316" stopOpacity={0.01} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="rgba(255,255,255,0.055)"
            />

            <XAxis
              dataKey="mes"
              tickLine={false}
              axisLine={false}
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
            />

            <YAxis
              tickFormatter={(v) => moedaCurta(v)}
              tickLine={false}
              axisLine={false}
              stroke="#94a3b8"
              tick={{ fontSize: 12 }}
              width={72}
            />

            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;

                return (
                  <div className="tooltipPremium">
                    <h4>{label || "Mês atual"}</h4>

                    {payload.map((entry, index) => (
                      <div className="tooltipLinha" key={index}>
                        <span
                          className="tooltipDot"
                          style={{ background: entry.color }}
                        />
                        <span>{entry.name}</span>
                        <strong>{moeda(entry.value)}</strong>
                      </div>
                    ))}
                  </div>
                );
              }}
            />

            <Legend wrapperStyle={{ paddingTop: 20, fontSize: 13 }} />

            <Area
              type="monotone"
              dataKey="faturamento"
              name="Faturamento"
              stroke="#3b82f6"
              fill="url(#fatGradientRede)"
              strokeWidth={4}
              dot={false}
              activeDot={{
                r: 7,
                strokeWidth: 3,
                stroke: "#020617",
                fill: "#60a5fa",
              }}
            />

            <Area
              type="monotone"
              dataKey="lucro"
              name="Lucro"
              stroke="#4ade80"
              fill="url(#lucroGradientRede)"
              strokeWidth={3}
              dot={false}
              activeDot={{
                r: 6,
                strokeWidth: 3,
                stroke: "#020617",
                fill: "#4ade80",
              }}
            />

            <Area
              type="monotone"
              dataKey="gastos"
              name="Gastos"
              stroke="#f97316"
              fill="url(#gastosGradientRede)"
              strokeWidth={2.5}
              dot={false}
              activeDot={{
                r: 6,
                strokeWidth: 3,
                stroke: "#020617",
                fill: "#f97316",
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ModalRelatorio({
  relatorio,
  moeda,
  fechar,
  alterarStatus,
  atualizandoId,
  carregando,
  calcularTotaisRelatorio,
}) {
  const comprovantes = relatorio.comprovantes || [];
  const tipo = relatorio.tipoRelatorio || "diario";
  const totais = calcularTotaisRelatorio(relatorio);
  const mensagemLimpa = limparMensagemColaboradores(relatorio.mensagemWhatsapp || "");

  return (
    <div className="modalOverlay" onClick={fechar}>
      <div className="modalContent" onClick={(e) => e.stopPropagation()}>
        <div className="modalHeader">
          <div>
            <span className="miniTitle">
              {tipo === "consolidado"
                ? "RELATÓRIO CONSOLIDADO"
                : "RELATÓRIO COMPLETO"}
            </span>
            <h2>{relatorio.usuarioInfo?.nomeEmpresa || relatorio.usuario}</h2>
            <p>{relatorio.data}</p>
          </div>

          <button className="btnFechar" onClick={fechar}>
            ✕
          </button>
        </div>

        {carregando ? (
          <div className="vazioClaro">Carregando relatório...</div>
        ) : (
          <>
            <div className="resumoWhatsapp">
              <pre>{mensagemLimpa}</pre>
            </div>

            <div className="modalGrid">
              <Card title="Entrada Total" value={moeda(totais.faturamento)} />
              <Card
                title="Gastos Tráfego"
                value={moeda(relatorio.gastoTrafego)}
              />
              <Card title="Total Gastos" value={moeda(totais.totalGastos)} />
              <Card
                title="Lucro Líquido"
                value={moeda(totais.lucroLiquido)}
                destaque
              />
              <Card title="Vendas Novas" value={totais.vendasNovas || 0} />
              <Card title="Renovações" value={totais.renovacoes || 0} />
            </div>

            {tipo === "consolidado" && relatorio.royalties?.ativo && (
              <div className="royaltiesBox">
                <h3>💎 Royalties</h3>
                <p>
                  {relatorio.royalties.nomeRecebedor}:{" "}
                  <strong>
                    {moeda(relatorio.royalties.valorRoyalties || 0)}
                  </strong>
                </p>
                <p>
                  {relatorio.royalties.nomeResponsavel}:{" "}
                  <strong>
                    {moeda(relatorio.royalties.valorRestante || 0)}
                  </strong>
                </p>
              </div>
            )}

            {Array.isArray(relatorio.devolucoes) && relatorio.devolucoes.length > 0 && (
              <div className="anexosBox">
                <h3>Devoluções / estornos</h3>
                {relatorio.devolucoes.map((item, index) => (
                  <div key={index} className="comprovanteLinha">
                    <span>
                      {item.descricao || "Devolução / estorno"} — {moeda(item.valor)}
                    </span>
                    {(item.comprovanteUrl || item.arquivoUrl) && (
                      <a
                        href={normalizarUrl(item.comprovanteUrl || item.arquivoUrl)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Abrir comprovante
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {Array.isArray(relatorio.outrosGastos) &&
              relatorio.outrosGastos.some(
                (item) => item.comprovanteUrl || item.arquivoUrl
              ) && (
                <div className="anexosBox">
                  <h3>Comprovantes de outros gastos</h3>

                  {relatorio.outrosGastos.map((item, index) => (
                    <div key={index} className="comprovanteLinha">
                      <span>
                        {item.descricao || "Outro gasto"} — {moeda(item.valor)}
                      </span>

                      {(item.comprovanteUrl || item.arquivoUrl) && (
                        <a
                          href={normalizarUrl(
                            item.comprovanteUrl || item.arquivoUrl
                          )}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Abrir comprovante
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}

            <AnexoBanco relatorio={relatorio} />

            {tipo === "consolidado" && (
              <AnexoTrafego relatorio={relatorio} />
            )}

            <div className="anexosBox">
              <h3>Comprovantes</h3>

              {tipo === "consolidado" ? (
                <p>Relatório consolidado não lista comprovantes individuais.</p>
              ) : comprovantes.length === 0 ? (
                <p>Nenhum comprovante encontrado para este dia.</p>
              ) : (
                comprovantes.map((comp) => (
                  <div key={comp._id} className="comprovanteLinha">
                    <span>
                      {comp.tipo} — {moeda(comp.valor)}
                    </span>

                    {comp.arquivoUrl && (
                      <a
                        href={normalizarUrl(comp.arquivoUrl)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Abrir
                      </a>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="acoesModal">
              <button
                className="btnAprovar"
                disabled={atualizandoId === relatorio._id}
                onClick={() => alterarStatus(relatorio._id, "aprovado", tipo)}
              >
                Aprovar
              </button>

              <button
                className="btnCorrecao"
                disabled={atualizandoId === relatorio._id}
                onClick={() =>
                  alterarStatus(relatorio._id, "aguardando_correcao", tipo)
                }
              >
                Solicitar correção
              </button>

              <button
                className="btnRejeitar"
                disabled={atualizandoId === relatorio._id}
                onClick={() => alterarStatus(relatorio._id, "rejeitado", tipo)}
              >
                Rejeitar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AnexoBanco({ relatorio }) {
  const [errosImagem, setErrosImagem] = useState({});

  const anexosOriginais = Array.isArray(relatorio?.anexosBanco)
    ? relatorio.anexosBanco
    : [];

  const anexos = anexosOriginais
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          url: normalizarUrl(item),
          tipo: "",
          nome: `Anexo ${index + 1}`,
        };
      }

      return {
        url: normalizarUrl(
          item?.url ||
            item?.bancoPrintUrl ||
            item?.linkAnexo ||
            item?.arquivoUrl ||
            ""
        ),
        tipo: String(
          item?.tipo || item?.bancoPrintTipo || item?.tipoArquivo || ""
        ).toLowerCase(),
        nome:
          item?.nome ||
          item?.originalname ||
          item?.filename ||
          `Anexo ${index + 1}`,
      };
    })
    .filter((item) => item.url);

  const urlLegada = normalizarUrl(
    relatorio?.bancoPrintUrl || relatorio?.linkAnexo || ""
  );

  if (urlLegada && !anexos.some((item) => item.url === urlLegada)) {
    anexos.unshift({
      url: urlLegada,
      tipo: String(relatorio?.bancoPrintTipo || "").toLowerCase(),
      nome: "Anexo do banco",
    });
  }

  const urlsVistas = new Set();
  const anexosUnicos = anexos.filter((item) => {
    if (!item.url || urlsVistas.has(item.url)) return false;
    urlsVistas.add(item.url);
    return true;
  });

  return (
    <div className="anexosBox">
      <h3>Print(s) do banco</h3>

      {anexosUnicos.length === 0 ? (
        <p>Nenhum print do banco anexado.</p>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          {anexosUnicos.map((anexo, index) => {
            const url = anexo.url;
            const tipo = String(anexo.tipo || "").toLowerCase();
            const chave = `${url}-${index}`;
            const erroImagem = Boolean(errosImagem[chave]);
            const ehPdf =
              tipo.includes("pdf") || String(url).toLowerCase().includes(".pdf");
            const ehImagem =
              tipo.includes("image") ||
              /\.(png|jpe?g|webp|gif|heic|heif)(\?|$)/i.test(String(url || ""));

            return (
              <div
                key={chave}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  display: "grid",
                  gap: 10,
                }}
              >
                <strong>
                  {index + 1}. {anexo.nome || `Anexo ${index + 1}`}
                </strong>

                {ehPdf ? (
                  <a href={url} target="_blank" rel="noreferrer">
                    Abrir PDF do banco
                  </a>
                ) : ehImagem ? (
                  <>
                    {!erroImagem && (
                      <img
                        src={url}
                        alt={`Print do banco ${index + 1}`}
                        loading="lazy"
                        decoding="async"
                        onError={() =>
                          setErrosImagem((atual) => ({ ...atual, [chave]: true }))
                        }
                        style={{
                          width: "100%",
                          maxHeight: 520,
                          objectFit: "contain",
                          borderRadius: 12,
                          background: "#fff",
                          display: "block",
                        }}
                      />
                    )}

                    <a href={url} target="_blank" rel="noreferrer">
                      {erroImagem
                        ? "Abrir imagem do banco"
                        : "Abrir imagem em nova aba"}
                    </a>

                    {erroImagem && (
                      <p className="erroImagem">
                        Não consegui carregar a prévia da imagem, mas você pode abrir o
                        anexo pelo botão acima.
                      </p>
                    )}
                  </>
                ) : (
                  <a href={url} target="_blank" rel="noreferrer">
                    Abrir arquivo do banco
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function AnexoTrafego({ relatorio }) {
  const [erroImagem, setErroImagem] = useState(false);

  const url = normalizarUrl(relatorio.trafegoPrintUrl);
  const tipo = String(relatorio.trafegoPrintTipo || "").toLowerCase();

  const ehPdf = tipo.includes("pdf") || String(url).toLowerCase().includes(".pdf");
  const podePreview = ehArquivoVisualizavelComoImagem(url, tipo);

  return (
    <div className="anexosBox">
      <h3>Print da saída do tráfego pago</h3>

      {!url ? (
        <p>Nenhum print da saída do tráfego pago anexado.</p>
      ) : ehPdf ? (
        <a href={url} target="_blank" rel="noreferrer">
          Abrir PDF do tráfego pago
        </a>
      ) : !podePreview ? (
        <a href={url} target="_blank" rel="noreferrer">
          Abrir arquivo do tráfego pago
        </a>
      ) : (
        <>
          {!erroImagem && (
            <img
              src={url}
              alt="Print da saída do tráfego pago"
              loading="lazy"
              decoding="async"
              onError={() => setErroImagem(true)}
            />
          )}

          <a href={url} target="_blank" rel="noreferrer">
            {erroImagem
              ? "Abrir imagem do tráfego pago"
              : "Abrir imagem do tráfego em nova aba"}
          </a>

          {erroImagem && (
            <p className="erroImagem">
              Não consegui carregar a prévia da imagem, mas você pode abrir o
              anexo pelo botão acima.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function limparMensagemColaboradores(texto) {
  if (!texto) return "";

  return String(texto)
    .replace(/\n\n👥 Colaboradores incluídos[\s\S]*$/g, "")
    .replace(/\n👥 Colaboradores incluídos[\s\S]*$/g, "")
    .trim();
}

function normalizarUrl(url) {
  if (!url) return "";

  const texto = String(url).trim();
  const apiBase = String(API_BASE || "").replace(/\/+$/, "");

  if (!texto) return "";

  if (texto.startsWith("http://") || texto.startsWith("https://")) {
    try {
      const parsed = new URL(texto);

      if (parsed.pathname.startsWith("/uploads")) {
        return `${apiBase}${parsed.pathname}${parsed.search || ""}`;
      }

      return texto;
    } catch {
      return texto;
    }
  }

  if (texto.startsWith("/uploads")) {
    return `${apiBase}${texto}`;
  }

  if (texto.startsWith("uploads")) {
    return `${apiBase}/${texto}`;
  }

  return texto;
}

function ehArquivoVisualizavelComoImagem(url, tipo = "") {
  const textoTipo = String(tipo || "").toLowerCase();
  const textoUrl = String(url || "").toLowerCase();

  if (textoTipo.includes("pdf") || textoUrl.includes(".pdf")) return false;
  if (textoTipo.includes("heic") || textoTipo.includes("heif")) return false;
  if (textoUrl.includes(".heic") || textoUrl.includes(".heif")) return false;

  if (textoTipo.startsWith("image/")) return true;
  return /\.(png|jpe?g|webp)(\?|$)/i.test(textoUrl);
}

function traduzirStatus(status) {
  const mapa = {
    pendente: "Pendente",
    aprovado: "Aprovado",
    rejeitado: "Rejeitado",
    aguardando_correcao: "Aguardando correção",
  };

  return mapa[status] || status || "Pendente";
}

function formatarMes(valor) {
  if (!valor) return "-";

  if (valor.includes(" de ")) return valor;

  const data = new Date(`${valor}-01T00:00:00`);

  if (Number.isNaN(data.getTime())) return valor;

  return data.toLocaleString("pt-BR", {
    month: "short",
    year: "2-digit",
  });
}

const css = `
.detalheContainer {
  max-width: 1600px;
  margin: 0 auto;
  padding-bottom: 60px;
  font-family: Inter, "Segoe UI", Roboto, Arial, sans-serif;
}

.topBar {
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.btnVoltar,
.btnAtualizar {
  border: none;
  background: #e2e8f0;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 900;
  cursor: pointer;
}

.btnAtualizar {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: white;
}

.heroDetalhe {
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.25), transparent 35%),
    linear-gradient(145deg,#020617,#0f172a);
  border-radius: 28px;
  padding: 28px;
  color: white;
  box-shadow: 0 25px 60px rgba(15,23,42,.25);
  margin-bottom: 18px;
}

.heroLeft {
  display: flex;
  gap: 18px;
  align-items: center;
}

.avatar {
  width: 80px;
  height: 80px;
  border-radius: 22px;
  overflow: hidden;
  background: linear-gradient(135deg,#2563eb,#7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 900;
  color: white;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.miniTitle {
  font-size: 12px;
  font-weight: 900;
  color: #93c5fd;
  letter-spacing: .08em;
}

.heroDetalhe h1 {
  margin: 6px 0;
  font-size: 42px;
}

.heroDetalhe p {
  margin: 0;
  color: #cbd5e1;
}

.badges {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
}

.badge.role {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge.saude {
  background: #dcfce7;
  color: #166534;
}


.periodoPainel {
  background:
    radial-gradient(circle at top right, rgba(37,99,235,0.18), transparent 32%),
    linear-gradient(145deg, #0f172a, #020617);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 26px;
  padding: 20px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin: 18px 0 10px;
  box-shadow: 0 20px 45px rgba(2,6,23,0.28);
}

.periodoPainel h2 {
  margin: 6px 0;
  font-size: clamp(24px, 4vw, 34px);
  letter-spacing: -0.04em;
}

.periodoPainel p {
  margin: 0;
  color: #cbd5e1;
}

.periodoTabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.periodoTabs button {
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.06);
  color: #dbeafe;
  padding: 11px 14px;
  border-radius: 999px;
  font-weight: 900;
  cursor: pointer;
  transition: transform .18s ease, background .18s ease, color .18s ease, box-shadow .18s ease;
}

.periodoTabs button:hover {
  transform: translateY(-1px);
  background: rgba(255,255,255,0.10);
}

.periodoTabs button.ativo {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: white;
  box-shadow: 0 12px 26px rgba(37,99,235,0.35);
}

.tituloSecao {
  margin: 34px 0 16px;
  font-size: clamp(24px, 4vw, 34px);
  font-weight: 800;
  letter-spacing: -1px;
  color: #0f172a;
}

.cardsGrid,
.modalGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 18px;
}

.card {
  min-height: 135px;
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.16), transparent 30%),
    radial-gradient(circle at bottom right, rgba(34,197,94,0.10), transparent 30%),
    linear-gradient(145deg, #0b1120, #020617);
  color: white;
  padding: 22px;
  border-radius: 26px;
  border: 1px solid rgba(255,255,255,0.04);
  box-shadow:
    0 20px 40px rgba(2,6,23,0.35),
    inset 0 1px 0 rgba(255,255,255,0.03);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 15px;
}

.card h2 {
  margin: 14px 0 0;
  font-size: clamp(20px, 1.45vw, 28px);
  font-weight: 700;
  letter-spacing: -0.4px;
  color: white;
  line-height: 1.05;
  white-space: nowrap;
}

.card h2.verde {
  color: #22c55e;
}

.crescimentoGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.growthCard {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.18), transparent 30%),
    radial-gradient(circle at bottom right, rgba(34,197,94,0.12), transparent 35%),
    linear-gradient(145deg, #0b1120, #020617);
  color: white;
  padding: 24px;
  border-radius: 26px;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 22px 45px rgba(2,6,23,0.35);
  min-height: 150px;
}

.growthTopo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.growthTopo span {
  color: #dbeafe;
  font-size: 15px;
}

.growthTopo small {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.08);
  color: #93c5fd;
  font-weight: 600;
}

.growthCard strong {
  display: block;
  margin-top: 22px;
  font-size: clamp(26px, 4vw, 38px);
  font-weight: 700;
  letter-spacing: -1px;
  line-height: 1;
}

.growthCard p {
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 14px;
}

.growthCard.positivo strong,
.growthCard.inicio strong {
  color: #22c55e;
}

.growthCard.negativo strong {
  color: #ef4444;
}

.growthCard.neutro strong {
  color: #94a3b8;
}

.graficoWrapper {
  margin-top: 8px;
}

.graficoTop {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.graficoTop h3 {
  margin: 0;
  color: #0f172a;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.8px;
}

.graficoTop p {
  margin-top: 8px;
  color: #64748b;
}

.graficoStats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.miniStat {
  background: white;
  border-radius: 18px;
  padding: 14px 16px;
  min-width: 160px;
  box-shadow: 0 10px 30px rgba(15,23,42,0.08);
}

.miniStat span {
  display: block;
  color: #64748b;
  font-size: 13px;
  margin-bottom: 8px;
}

.miniStat strong {
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.graficoBox {
  background:
    radial-gradient(circle at top left, rgba(59,130,246,0.14), transparent 25%),
    radial-gradient(circle at bottom right, rgba(34,197,94,0.10), transparent 25%),
    linear-gradient(145deg, #030712, #020617);
  border-radius: 30px;
  padding: 24px;
  border: 1px solid rgba(255,255,255,0.04);
  box-shadow:
    0 25px 60px rgba(2,6,23,0.40),
    inset 0 1px 0 rgba(255,255,255,0.03);
  overflow: hidden;
  margin-bottom: 24px;
}

.tooltipPremium {
  background: rgba(255,255,255,0.96);
  padding: 16px;
  border-radius: 18px;
  box-shadow: 0 18px 35px rgba(15,23,42,0.18);
  min-width: 220px;
}

.tooltipPremium h4 {
  margin: 0 0 14px;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
}

.tooltipLinha {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.tooltipLinha strong {
  margin-left: auto;
  color: #0f172a;
  font-weight: 700;
}

.tooltipDot {
  width: 12px;
  height: 12px;
  border-radius: 999px;
}

.vazioPremium {
  text-align: center;
  padding: 70px 30px;
}

.vazioIcone {
  font-size: 48px;
  margin-bottom: 16px;
}

.vazioPremium h3 {
  color: white;
  margin-bottom: 10px;
  font-weight: 700;
}

.vazioPremium p {
  color: #94a3b8;
}

.bloco {
  background: white;
  border-radius: 24px;
  padding: 20px;
  margin-bottom: 18px;
  box-shadow: 0 14px 30px rgba(15,23,42,.08);
}

.blocoHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
}

.blocoHeader h2 {
  margin: 0;
  font-size: 24px;
  color: #0f172a;
}

.contador {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 900;
}

.relatorioCard {
  background: #f8fafc;
  padding: 16px;
  border-radius: 18px;
  margin-bottom: 12px;
}

.relatorioTop {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.relatorioTop p {
  margin: 4px 0 0;
  color: #64748b;
}

.relatorioValores {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.statusRelatorio {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  height: fit-content;
}

.statusRelatorio.pendente {
  background: #fef3c7;
  color: #92400e;
}

.statusRelatorio.aprovado {
  background: #dcfce7;
  color: #166534;
}

.statusRelatorio.rejeitado {
  background: #fee2e2;
  color: #b91c1c;
}

.statusRelatorio.aguardando_correcao {
  background: #dbeafe;
  color: #1d4ed8;
}

.observacao {
  background: white;
  border-left: 4px solid #2563eb;
  padding: 10px 12px;
  border-radius: 12px;
  color: #334155;
  margin-bottom: 12px;
}

.acoesRelatorio,
.acoesModal {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.btnAbrirRelatorio,
.btnAprovar,
.btnCorrecao,
.btnRejeitar {
  border: none;
  width: auto !important;
  min-width: auto;
  flex: 0 0 auto;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 900;
  cursor: pointer;
  color: white;
  white-space: nowrap;
  line-height: 1;
}

.btnAbrirRelatorio {
  background: #0f172a;
}

.btnAprovar {
  background: #16a34a;
}

.btnCorrecao {
  background: #2563eb;
}

.btnRejeitar {
  background: #dc2626;
}

button:disabled {
  opacity: .65;
  cursor: not-allowed;
}

.loadingBox,
.vazioClaro {
  background: white;
  padding: 22px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(15,23,42,.08);
  color: #64748b;
}

.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(2,6,23,.72);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px;
  overflow-y: auto;
}

.modalContent {
  width: 100%;
  max-width: 980px;
  background: #f1f5f9;
  border-radius: 26px;
  padding: 22px;
  box-shadow: 0 30px 80px rgba(0,0,0,.35);
}

.modalHeader {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.modalHeader h2 {
  margin: 6px 0;
  color: #0f172a;
}

.modalHeader p {
  margin: 0;
  color: #64748b;
}

.btnFechar {
  border: none;
  background: #0f172a;
  color: white;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  font-weight: 900;
  cursor: pointer;
}

.resumoWhatsapp {
  background: #020617;
  color: #dcfce7;
  border-radius: 20px;
  padding: 18px;
  margin-bottom: 16px;
  overflow-x: auto;
}

.resumoWhatsapp pre {
  margin: 0;
  white-space: pre-wrap;
  font-family: inherit;
  line-height: 1.6;
}

.royaltiesBox,
.anexosBox {
  background: white;
  padding: 18px;
  border-radius: 20px;
  margin-bottom: 14px;
  box-shadow: 0 10px 24px rgba(15,23,42,.06);
}

.royaltiesBox h3,
.anexosBox h3 {
  margin: 0 0 12px;
  color: #0f172a;
}

.royaltiesBox p {
  margin: 8px 0;
}

.anexosBox img {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 16px;
  display: block;
  margin-bottom: 12px;
  background: white;
  object-fit: contain;
}

.anexosBox a {
  display: inline-flex;
  background: #2563eb;
  color: white;
  padding: 10px 14px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 900;
  margin-right: 8px;
  margin-top: 8px;
}

.erroImagem {
  color: #991b1b;
  font-weight: 700;
  margin-top: 10px;
}

.comprovanteLinha {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  background: #f8fafc;
  padding: 12px;
  border-radius: 14px;
  margin-bottom: 8px;
  align-items: center;
}

.assistidoBox,
.formAssistido {
  background: linear-gradient(135deg, #eff6ff, #ffffff);
  border: 1px solid #bfdbfe;
  border-radius: 22px;
  padding: 22px;
  margin-top: 18px;
  box-shadow: 0 10px 25px rgba(37, 99, 235, .08);
}

.assistidoBox {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
}

.assistidoAcoes {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.assistidoBox h2,
.formAssistido h2 {
  color: #0f172a;
  margin: 5px 0 7px;
}

.assistidoBox p,
.formAssistido p {
  color: #475569;
  margin: 0;
}

.btnAssistido,
.btnSalvarAssistido {
  border: 0;
  border-radius: 12px;
  color: white;
  background: linear-gradient(135deg, #2563eb, #0891b2);
  font-weight: 800;
  cursor: pointer;
  padding: 13px 18px;
  white-space: nowrap;
}

.btnAssistido.secundario {
  background: #0f172a;
}

.formAssistido {
  background: white;
}

.formAssistidoHeader {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.assistidoSeguro {
  background: #dcfce7;
  color: #166534;
  padding: 8px 11px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.assistidoGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.campoAssistido {
  display: grid;
  gap: 7px;
  color: #1e3a5f;
  font-size: 13px;
  font-weight: 800;
}

.campoAssistido input,
.campoAssistido textarea,
.campoAssistido select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #bfdbfe;
  border-radius: 11px;
  padding: 11px 12px;
  color: #0f172a;
  font: inherit;
  font-weight: 600;
  outline: none;
}

.campoAssistido input:focus,
.campoAssistido textarea:focus,
.campoAssistido select:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
}

.campoObservacao {
  margin-top: 14px;
}

.campoAssistido textarea {
  min-height: 72px;
  resize: vertical;
}

.assistidoAviso {
  color: #475569;
  background: #f8fafc;
  border-left: 4px solid #38bdf8;
  border-radius: 10px;
  padding: 11px 12px;
  margin: 16px 0;
  font-size: 13px;
}

@media (max-width: 768px) {
  .topBar {
    display: grid;
    grid-template-columns: 1fr;
  }

  .heroDetalhe {
    padding: 22px;
    border-radius: 22px;
  }

  .heroLeft {
    flex-direction: column;
    align-items: flex-start;
  }

  .heroDetalhe h1 {
    font-size: 32px;
  }

  .periodoPainel {
    flex-direction: column;
    align-items: stretch;
  }

  .periodoTabs {
    justify-content: flex-start;
  }

  .periodoTabs button {
    flex: 1 1 calc(50% - 8px);
  }

  .cardsGrid,
  .modalGrid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
  }

.card h2 {
  margin: 14px 0 0;
  font-size: clamp(21px, 1.65vw, 30px);
  font-weight: 700;
  letter-spacing: -0.8px;
  color: white;
  line-height: 1;
  white-space: nowrap;
}

.card {
  min-height: 122px;
  padding: 24px 28px;
  overflow: hidden;
}

  .growthCard {
    min-height: 135px;
    padding: 20px;
  }

  .graficoTop {
    flex-direction: column;
  }

  .graficoStats {
    display: grid;
    grid-template-columns: 1fr;
  }

  .miniStat {
    min-width: 100%;
  }

  .graficoBox {
    padding: 12px;
    border-radius: 24px;
  }

  .bloco,
  .modalContent {
    padding: 16px;
    border-radius: 18px;
  }

  .relatorioTop {
    flex-direction: column;
    align-items: flex-start;
  }

  .acoesRelatorio,
  .acoesModal {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }

  .btnAbrirRelatorio,
  .btnAprovar,
  .btnCorrecao,
  .btnRejeitar {
    width: auto !important;
    min-height: 40px;
    padding: 9px 13px;
    flex: 0 0 auto;
  }

  .modalOverlay {
    padding: 12px;
  }

  .comprovanteLinha {
    flex-direction: column;
    align-items: stretch;
  }

  .assistidoBox,
  .formAssistidoHeader {
    flex-direction: column;
    align-items: stretch;
  }

  .assistidoGrid {
    grid-template-columns: 1fr;
  }

  .btnAssistido,
  .btnSalvarAssistido {
    width: 100%;
  }

  .assistidoAcoes {
    display: grid;
    width: 100%;
  }
}

@media (max-width: 390px) {
  .periodoPainel {
    flex-direction: column;
    align-items: stretch;
  }

  .periodoTabs {
    justify-content: flex-start;
  }

  .periodoTabs button {
    flex: 1 1 calc(50% - 8px);
  }

  .cardsGrid,
  .modalGrid {
    grid-template-columns: 1fr;
  }

  .graficoTop h3 {
    font-size: 24px;
  }
}
`;
